import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import {
  downloadUtilityReportForCity,
  downloadUtilityReportForCitySuccess,
  downloadUtilityReportForCityError,
  downloadUtilityReportForFacility,
  downloadUtilityReportForFacilitySuccess,
  downloadUtilityReportForFacilityError,
  downloadUtilityReportForGroup,
  downloadUtilityReportForGroupSuccess,
  downloadUtilityReportForGroupError,
  downloadUtilityReportForUtilityContract,
  downloadUtilityReportForUtilityContractSuccess,
  downloadUtilityReportForUtilityContractError,
  getUtilityReportDataForCity,
  getUtilityReportDataForCityError,
  getUtilityReportDataForCitySuccess,
  getUtilityReportDataForFacility,
  getUtilityReportDataForFacilityContract,
  getUtilityReportDataForFacilityContractError,
  getUtilityReportDataForFacilityContractSuccess,
  getUtilityReportDataForFacilityError,
  getUtilityReportDataForFacilitySuccess,
  getUtilityReportDataForGroup,
  getUtilityReportDataForGroupError,
  getUtilityReportDataForGroupSuccess,
  UtilityReportExportFormat,
} from '../actions/utility-report.actions';
import { UtilityReportService } from '../../service/utility/utility-report.service';
import { saveAs } from 'file-saver';
import { State } from '../../../reducers';
import { Store } from '@ngrx/store';
import { selectFacilities, selectFacilityGroups } from '../selectors/facility.selector';

@Injectable()
export class UtilityReportEffects {
  downloadUtilityReportForFacilityEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(downloadUtilityReportForFacility),
      concatLatestFrom(() => this.store.select(selectFacilities)),
      switchMap(([{ facilityId, startDate, endDate, utilityType, detailed, exportFormat }, facilities]) => {
        const facilityName = facilities.find(facility => facility.id === facilityId)?.name ?? 'facility';
        return this.utilityReportService.downloadFacilityUtilityReport(facilityId, startDate, endDate, utilityType, detailed, exportFormat || 'pdf').pipe(
          tap(content => {
            const { extension, mimeType } = reportFileMeta(exportFormat);
            const blob = new Blob([content], { type: mimeType });
            saveAs(blob, `${utilityType || 'utility'} report - ${facilityName}.${extension}`);
          }),
          map(() => downloadUtilityReportForFacilitySuccess()),
          catchError(error => of(downloadUtilityReportForFacilityError({ error }))),
        );
      }),
    );
  });

  downloadUtilityReportForUtilityContractEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(downloadUtilityReportForUtilityContract),
      switchMap(({ facilityId, utilityContractNumber, startDate, endDate, detailed, exportFormat }) => {
        return this.utilityReportService
          .downloadUtilityContractReport(facilityId, utilityContractNumber, startDate, endDate, detailed, exportFormat || 'pdf')
          .pipe(
          tap(content => {
            const { extension, mimeType } = reportFileMeta(exportFormat);
            const blob = new Blob([content], { type: mimeType });
            saveAs(blob, `${utilityContractNumber}.${extension}`);
          }),
          map(() => downloadUtilityReportForUtilityContractSuccess()),
          catchError(error => of(downloadUtilityReportForUtilityContractError({ error }))),
        );
      }),
    );
  });

  downloadUtilityReportForGroupEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(downloadUtilityReportForGroup),
      concatLatestFrom(() => this.store.select(selectFacilityGroups)),
      switchMap(([{ facilityGroupId, startDate, endDate, utilityType, detailed, exportFormat }, groups]) => {
        const facilityGroupName = groups.find(group => group.id === facilityGroupId)?.name ?? 'group';
        return this.utilityReportService
          .downloadFacilityGroupUtilityReport(facilityGroupId, startDate, endDate, utilityType, detailed, exportFormat || 'pdf')
          .pipe(
          tap(content => {
            const { extension, mimeType } = reportFileMeta(exportFormat);
            const blob = new Blob([content], { type: mimeType });
            saveAs(blob, `${utilityType || 'utility'} report - ${facilityGroupName}.${extension}`);
          }),
          map(() => downloadUtilityReportForGroupSuccess()),
          catchError(error => of(downloadUtilityReportForGroupError({ error }))),
        );
      }),
    );
  });

  downloadUtilityReportForCityEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(downloadUtilityReportForCity),
      switchMap(({ startDate, endDate, utilityType, detailed, exportFormat }) =>
        this.utilityReportService.downloadCityUtilityReport(startDate, endDate, utilityType, detailed, exportFormat || 'pdf').pipe(
          tap(content => {
            const { extension, mimeType } = reportFileMeta(exportFormat);
            const blob = new Blob([content], { type: mimeType });
            saveAs(blob, `${utilityType || 'utility'} report - city.${extension}`);
          }),
          map(() => downloadUtilityReportForCitySuccess()),
          catchError(error => of(downloadUtilityReportForCityError({ error }))),
        ),
      ),
    );
  });

  getUtilityReportDataForFacilityEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getUtilityReportDataForFacility),
      switchMap(({ facilityId, startDate, endDate }) => {
        return this.utilityReportService.getFacilityUtilityReportData(facilityId, startDate, endDate).pipe(
          map(data => getUtilityReportDataForFacilitySuccess({ data })),
          catchError(error => of(getUtilityReportDataForFacilityError({ error }))),
        );
      }),
    );
  });

  getUtilityReportDataForFacilityContractEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getUtilityReportDataForFacilityContract),
      switchMap(({ facilityId, contractNumber, startDate, endDate }) => {
        return this.utilityReportService.getFacilityUtilityContractReportData(facilityId, contractNumber, startDate, endDate).pipe(
          map(data => getUtilityReportDataForFacilityContractSuccess({ data })),
          catchError(error => of(getUtilityReportDataForFacilityContractError({ error }))),
        );
      }),
    );
  });

  getUtilityReportDataForGroupEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getUtilityReportDataForGroup),
      switchMap(({ facilityGroupId, startDate, endDate }) => {
        return this.utilityReportService.getFacilityGroupUtilityReportData(facilityGroupId, startDate, endDate).pipe(
          map(data => getUtilityReportDataForGroupSuccess({ data })),
          catchError(error => of(getUtilityReportDataForGroupError({ error }))),
        );
      }),
    );
  });

  getUtilityReportDataForCityEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getUtilityReportDataForCity),
      switchMap(({ startDate, endDate }) =>
        this.utilityReportService.getCityUtilityReportData(startDate, endDate).pipe(
          map(data => getUtilityReportDataForCitySuccess({ data })),
          catchError(error => of(getUtilityReportDataForCityError({ error }))),
        ),
      ),
    );
  });

  constructor(
    private actions$: Actions,
    private utilityReportService: UtilityReportService,
    private store: Store<State>,
  ) {}
}

function reportFileMeta(exportFormat?: UtilityReportExportFormat): { extension: string; mimeType: string } {
  switch (exportFormat) {
    case 'csv':
      return { extension: 'csv', mimeType: 'text/csv;charset=utf-8' };
    case 'xlsx':
      return {
        extension: 'xlsx',
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      };
    default:
      return { extension: 'pdf', mimeType: 'application/pdf' };
  }
}
